import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { motion } from "framer-motion";

/**
 * Decorative animated tile grid — sits absolutely behind a panel (parent needs position:"relative").
 * Colors are passed in by the caller so it follows the current theme/mode.
 * Grid dimensions are measured from the container itself (via ResizeObserver) so the tiles
 * always fill the panel edge-to-edge, whether it's a ~60%-width split-panel or the full viewport.
 */
export function TilesBackground({ tileSize = 44, borderColor, hoverColor }) {
  const containerRef = useRef(null);
  const [grid, setGrid] = useState({ rows: 0, cols: 0 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const measure = () => {
      const { width, height } = el.getBoundingClientRect();
      setGrid({
        cols: Math.ceil(width / tileSize) + 1,
        rows: Math.ceil(height / tileSize) + 1,
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [tileSize]);

  const rowsArray = new Array(grid.rows).fill(0);
  const colsArray = new Array(grid.cols).fill(0);

  return (
    <Box
      ref={containerRef}
      sx={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        zIndex: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {rowsArray.map((_, i) => (
        <Box key={`tile-row-${i}`} sx={{ display: "flex", flexShrink: 0 }}>
          {colsArray.map((_, j) => (
            <motion.div
              key={`tile-col-${j}`}
              whileHover={{ backgroundColor: hoverColor }}
              transition={{ duration: 0.15 }}
              style={{
                width: tileSize,
                height: tileSize,
                flexShrink: 0,
                borderRight: `1px solid ${borderColor}`,
                borderBottom: `1px solid ${borderColor}`,
              }}
            />
          ))}
        </Box>
      ))}
    </Box>
  );
}

export default TilesBackground;
